import React, { useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import { Select } from "antd";
import { Spin } from "antd";
import "antd/dist/reset.css";
import { getRequest } from "../service/index";
import { getEmployeeData } from '../utils/localStorage';
import { getUserIanaTimeZone, nowInUserTz } from '../utils/dateTime';
import { dayjs } from '../utils/dateTime';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

const Chart = () => {
  const userTimeZone = getUserIanaTimeZone();

  // Helper function to calculate working hours correctly (handles overnight shifts)
  const calculateWorkingHours = (checkIn, checkOut) => {
    if (!checkIn || !checkOut || checkIn === '-' || checkOut === '-') {
      return 0;
    }

    // Parse time strings (HH:mm)
    const [inHours, inMinutes] = checkIn.split(':').map(Number);
    const [outHours, outMinutes] = checkOut.split(':').map(Number);

    // Convert to minutes since midnight
    let inTotalMinutes = inHours * 60 + inMinutes;
    let outTotalMinutes = outHours * 60 + outMinutes;

    // If check-out is before check-in, assume it's next day (overnight shift)
    if (outTotalMinutes <= inTotalMinutes) {
      outTotalMinutes += 24 * 60;
    }

    const totalMinutes = outTotalMinutes - inTotalMinutes;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return hours + (minutes / 60);
  };

  // Helper to format working hours for display
  const formatWorkingHours = (hours) => {
    if (!hours || hours === 0) return '0:00';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  const getLast3Months = (currentDate) => {
    const now = dayjs.tz(currentDate, userTimeZone);
    const months = [];
    for (let i = 0; i >= -2; i--) {
      const d = now.clone().add(i, 'month');
      months.push({
        value: d.format('YYYY-MM'),
        label: d.format('MMMM'),
        year: d.year(),
        month: d.month()
      });
    }
    return months;
  };

  const currentDate = nowInUserTz().toDate();
  const last3Months = getLast3Months(currentDate);

  const [selectedMonth, setSelectedMonth] = useState(last3Months[0].value);
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shift, setShift] = useState(null);

  const legend = [
    { label: "Present", color: "#231564" },
    { label: "Absent", color: "#ff0000" },
    { label: "HalfDay", color: "#39bba0" },
    { label: "ShortDay", color: "#f79824" },
    { label: "Leave", color: "#e2e97a" },
    { label: "Missing", color: "#a93784" },
    { label: "OFF", color: "#000000" },
    { label: "Late", color: "#feca3f" },
    { label: "Early", color: "#cf3b1f" },
    { label: "Holiday", color: "#119865" },
    { label: "SchDays", color: "#5d7d87" },
  ];

  useEffect(() => {
    const fetchAttendance = async () => {
      setLoading(true);
      try {
        const userData = getEmployeeData();
        const [year, month] = selectedMonth.split("-").map(Number);
        
        const startDateObj = dayjs.tz(new Date(year, month - 1, 1), userTimeZone);
        const endDateObj = startDateObj.clone().endOf('month');
        
        const startDate = startDateObj.format('YYYY-MM-DD');
        const endDate = endDateObj.format('YYYY-MM-DD');
        const employeeId = userData?.employeeId;

        if (!employeeId) {
          setAttendanceData([]);
          setLoading(false);
          return;
        }

        const shiftRes = await getRequest(`/api/companies/shifts/byEmployeeId?employeeId=${employeeId}`);
        const workingDays = shiftRes?.shift?.workingDays || [];
        setShift(shiftRes?.shift || null);

        const res = await getRequest(
          `/api/myattendance?employeeId=${employeeId}&startDate=${startDate}&endDate=${endDate}`
        );
        let logs = res?.attendanceLogs || [];
        
        const daysMap = {};
        logs.forEach(log => {
          const logDate = dayjs.tz(log.date, userTimeZone);
          const day = logDate.date();
          daysMap[day] = log;
        });
        
        const joiningDateStr = userData?.joiningDate;
        let joiningDate = null;
        if (joiningDateStr) {
          joiningDate = dayjs.tz(joiningDateStr, userTimeZone).startOf('day');
        }
        
        const today = dayjs.tz(new Date(), userTimeZone).startOf('day');
        const daysInMonth = endDateObj.date();
        
        const monthData = [];
        for (let day = 1; day <= daysInMonth; day++) {
          const dateObj = dayjs.tz(new Date(year, month - 1, day), userTimeZone).startOf('day');
          const dayName = dateObj.format('dddd');

          if (joiningDate && dateObj.isBefore(joiningDate)) {
            monthData.push({
              date: dateObj.format('YYYY-MM-DD'),
              status: "SchDays",
              hours: 1,
              checkIn: null,
              checkOut: null,
              day: day,
              monthShort: dateObj.format('MMM')
            });
            continue;
          }

          if (!workingDays.includes(dayName)) {
            monthData.push({
              date: dateObj.format('YYYY-MM-DD'),
              status: "OFF",
              hours: 1,
              checkIn: null,
              checkOut: null,
              day: day,
              monthShort: dateObj.format('MMM'),
              workingHour: null
            });
          } else if (daysMap[day]) {
            const log = daysMap[day];
            
            // Calculate hours correctly using the helper function
            let hoursDecimal = 0;
            let workingHourStr = log.workingHour;
            
            // If we have check-in and check-out, calculate hours properly
            if (log.checkIn && log.checkOut && log.checkIn !== '-' && log.checkOut !== '-') {
              hoursDecimal = calculateWorkingHours(log.checkIn, log.checkOut);
              workingHourStr = formatWorkingHours(hoursDecimal);
            } else if (workingHourStr && typeof workingHourStr === 'string' && workingHourStr.includes(':')) {
              // Fallback to workingHour if available
              const [h, m] = workingHourStr.split(":").map(Number);
              hoursDecimal = h + m / 60;
            } else if (typeof log.reqHour === 'number') {
              hoursDecimal = log.reqHour;
              workingHourStr = null;
            }
            
            let status = log.status;
            if (status === "Late & Early" || status === "Incomplete") {
              status = "ShortDay";
            }
            if (status === "half") {
              status = "HalfDay";
            }
            
            monthData.push({
              date: log.date,
              status: status,
              hours: hoursDecimal,
              checkIn: log.checkIn,
              checkOut: log.checkOut,
              day: day,
              monthShort: dateObj.format('MMM'),
              workingHour: workingHourStr,
              // Store original data for debugging
              _originalCheckIn: log.checkIn,
              _originalCheckOut: log.checkOut
            });
          } else {
            const isFutureDate = dateObj.isAfter(today);
            
            monthData.push({
              date: dateObj.format('YYYY-MM-DD'),
              status: isFutureDate ? "SchDays" : "Absent",
              hours: 1,
              checkIn: null,
              checkOut: null,
              day: day,
              monthShort: dateObj.format('MMM'),
              workingHour: null
            });
          }
        }
        
        const coloredData = monthData.map(entry => {
          const statusColor = legend.find(item => item.label === entry.status)?.color;
          return { ...entry, color: statusColor || "#231564" };
        });
        setAttendanceData(coloredData);
      } catch (err) {
        console.error('Error fetching attendance:', err);
        setAttendanceData([]);
      }
      setLoading(false);
    };
    fetchAttendance();
  }, [selectedMonth, userTimeZone]);

  const [year, month] = selectedMonth.split("-").map(Number);
  const daysInMonth = new Date(year, month, 0).getDate();
  const monthShort = nowInUserTz().year(year).month(month - 1).format('MMM');
  const dayLabels = Array.from({ length: daysInMonth }, (_, i) => `${i + 1}-${monthShort}`);

  const chartData = {
    labels: dayLabels,
    datasets: [
      {
        label: "Attendance",
        data: attendanceData.map((entry) => entry.hours),
        backgroundColor: attendanceData.map((entry) => entry.color),
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        max: 15,
        ticks: {
          stepSize: 3,
          callback: function (value) {
            return value.toFixed(2);
          }
        }
      },
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const dataIndex = context.dataIndex;
            const entry = attendanceData[dataIndex];
            if (!entry) return '';
            
            const checkInTime = entry.checkIn || 'No check-in';
            const checkOutTime = entry.checkOut || 'No check-out';
            
            // Show working hours in HH:MM format in tooltip
            const hours = entry.hours || 0;
            const h = Math.floor(hours);
            const m = Math.round((hours - h) * 60);
            const workingHourDisplay = entry.workingHour || `${h}:${m.toString().padStart(2, '0')}`;
            
            return [
              `Working Hours: ${workingHourDisplay}`,
              `Check-in: ${checkInTime}`,
              `Check-out: ${checkOutTime}`
            ];
          }
        }
      }
    }
  };

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
  };

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "20px",
          flexWrap: "wrap",
          alignItems: "center"
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          {legend.map((item) => (
            <div
              key={item.label}
              style={{
                marginRight: "10px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  width: "30px",
                  height: "15px",
                  backgroundColor: item.color,
                  marginRight: "5px",
                }}
              ></div>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
        <div>
          <Select
            value={selectedMonth}
            onChange={handleMonthChange}
            style={{
              minWidth: 120,
              fontSize: "16px",
              borderRadius: "4px",
            }}
            options={last3Months.map(m => ({
              value: m.value,
              label: m.label
            }))}
            placement="bottomRight"
          />
        </div>
      </div>
      <div style={{ height: "300px" }}>
        {loading ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin />
          </div>
        ) : (
          <Bar data={chartData} options={options} />
        )}
      </div>
    </div>
  );
};

export default Chart;